<section class="configuration-diagnostics">
  <form class="diagnostics-form" [formGroup]="diagnosticsForm" (ngSubmit)="runDiagnostics()">
    <div class="field">
      <label for="configurationCode">Configuration</label>
      <input id="configurationCode" type="text" formControlName="configurationCode" autocomplete="off" />
    </div>

    <div class="field">
      <label for="scopeTypeCode">Scope Type</label>
      <input id="scopeTypeCode" type="text" formControlName="scopeTypeCode" autocomplete="off" />
    </div>

    <div class="field">
      <label for="scopeKey">Scope Key</label>
      <input id="scopeKey" type="text" formControlName="scopeKey" autocomplete="off" />
    </div>

    <div class="field">
      <label for="asOfUtc">As Of UTC</label>
      <input id="asOfUtc" type="datetime-local" formControlName="asOfUtc" />
    </div>

    <div class="field">
      <label for="localeCode">Terminology Locale</label>
      <input id="localeCode" type="text" formControlName="localeCode" autocomplete="off" />
    </div>

    <div class="field">
      <label for="channelCode">Terminology Channel</label>
      <input id="channelCode" type="text" formControlName="channelCode" autocomplete="off" />
    </div>

    <div class="field">
      <label for="audienceCode">Terminology Audience</label>
      <input id="audienceCode" type="text" formControlName="audienceCode" autocomplete="off" />
    </div>

    <div class="field">
      <label for="tenantId">Terminology Tenant</label>
      <input id="tenantId" type="text" formControlName="tenantId" autocomplete="off" />
    </div>

    <div class="field">
      <label for="domainCode">Lookup Domain</label>
      <input id="domainCode" type="text" formControlName="domainCode" autocomplete="off" />
    </div>

    <div class="field">
      <label for="entityCode">Entity</label>
      <input id="entityCode" type="text" formControlName="entityCode" autocomplete="off" />
    </div>

    <div class="field">
      <label for="subjectCode">Rule Subject</label>
      <input id="subjectCode" type="text" formControlName="subjectCode" autocomplete="off" />
    </div>

    <div class="field">
      <label for="bindingTypeCode">Binding Type</label>
      <input id="bindingTypeCode" type="text" formControlName="bindingTypeCode" autocomplete="off" />
    </div>

    <div class="actions">
      <button type="submit" [disabled]="isLoading()">Run Diagnostics</button>
      <button type="button" class="secondary" [disabled]="isLoading()" (click)="resetDiagnostics()">Reset</button>
    </div>
  </form>

  <div *ngIf="diagnosticsForm.controls.configurationCode.invalid && diagnosticsForm.controls.configurationCode.touched" class="state invalid" data-testid="validation-state">
    Configuration is required.
  </div>

  <div *ngIf="isLoading()" class="state loading" data-testid="loading-state">
    Loading diagnostics
  </div>

  <div *ngIf="!isLoading() && !hasAnyResult()" class="state" data-testid="initial-state">
    No diagnostics loaded.
  </div>

  <div *ngIf="!isLoading() && resolutionResult() as resolution" class="diagnostic-section" data-testid="resolution-section">
    <header>
      <h2>Resolution</h2>
      <span [class]="'badge ' + resolution.state">{{ resolution.state }}</span>
    </header>

    <p *ngIf="resolution.state !== 'resolved'" class="message">{{ formatMessage(resolution) }}</p>

    <dl *ngIf="resolution.data?.version as version" class="definition-list">
      <div>
        <dt>Version</dt>
        <dd>{{ version.versionCode }}</dd>
      </div>
      <div>
        <dt>Scope</dt>
        <dd>{{ version.scopeTypeCode || version.matchedDimensions?.["scopeTypeCode"] || "global" }}</dd>
      </div>
      <div>
        <dt>Key</dt>
        <dd>{{ version.scopeKey || version.matchedDimensions?.["scopeKey"] || "none" }}</dd>
      </div>
      <div>
        <dt>Matches</dt>
        <dd>{{ resolution.data?.matchedBindingCount }}</dd>
      </div>
    </dl>

    <table *ngIf="resolutionTrace().length > 0">
      <thead>
        <tr>
          <th>Scope Type</th>
          <th>Scope Key</th>
          <th>Candidates</th>
          <th>Matches</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let trace of resolutionTrace()">
          <td>{{ trace.scopeTypeCode || "global" }}</td>
          <td>{{ trace.scopeKey || "none" }}</td>
          <td>{{ trace.candidateBindingCount ?? 0 }}</td>
          <td>{{ trace.matchedBindingCount ?? 0 }}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <div *ngIf="terminologyHealthResult() as terminologyHealth" class="diagnostic-section" data-testid="terminology-health-section">
    <header>
      <h2>Terminology Health</h2>
      <span
        *ngIf="terminologyHealth.data?.summary as summary; else terminologyHealthState"
        [class]="'badge ' + (summary.isHealthy ? 'resolved' : 'conflict')"
      >{{ summary.isHealthy ? "healthy" : "issues" }}</span>
      <ng-template #terminologyHealthState>
        <span [class]="'badge ' + terminologyHealth.state">{{ terminologyHealth.state }}</span>
      </ng-template>
    </header>

    <p *ngIf="terminologyHealth.state !== 'resolved'" class="message">{{ formatMessage(terminologyHealth) }}</p>
    <p *ngIf="terminologyHealth.data?.summary?.isHealthy" class="message">
      All selected terminology tokens resolve without runtime health issues.
    </p>

    <dl *ngIf="terminologyHealth.data?.summary as summary" class="definition-list">
      <div>
        <dt>Candidates</dt>
        <dd>{{ summary.candidateCount }}</dd>
      </div>
      <div>
        <dt>Tokens</dt>
        <dd>{{ summary.tokenCount }}</dd>
      </div>
      <div>
        <dt>Resolved</dt>
        <dd>{{ summary.resolvedTokenCount }}</dd>
      </div>
      <div>
        <dt>Errors</dt>
        <dd>{{ summary.errorCount }}</dd>
      </div>
      <div>
        <dt>Warnings</dt>
        <dd>{{ summary.warningCount }}</dd>
      </div>
    </dl>

    <dl *ngIf="terminologyHealth.data?.freshness as freshness" class="definition-list" data-testid="terminology-freshness">
      <div>
        <dt>Active revision</dt>
        <dd><code>{{ freshness.revisionToken }}</code></dd>
      </div>
      <div>
        <dt>Response age</dt>
        <dd>{{ formatCacheAge(freshness.resolvedAtUtc) }}</dd>
      </div>
      <div>
        <dt>Navigation revalidation</dt>
        <dd>{{ freshness.navigationRevalidateAfterSeconds }} seconds</dd>
      </div>
      <div>
        <dt>Active-session refresh</dt>
        <dd>{{ freshness.activeSessionRefreshAfterSeconds }} seconds</dd>
      </div>
    </dl>

    <section *ngIf="terminologyHealth.data?.usageEvidence as usageEvidence" class="usage-evidence" data-testid="terminology-usage-evidence">
      <header>
        <h3>Deployed Usage Evidence</h3>
        <span [class]="'badge ' + usageEvidence.statusCode">{{ usageEvidence.statusCode }}</span>
      </header>

      <ng-container *ngIf="usageEvidence.statusCode === 'available'; else usageEvidenceUnavailable">
        <p class="message">
          This compares evaluated tokens with typed client and server declarations in release
          <code>{{ usageEvidence.releaseIdentifier }}</code>. An unused candidate has no declaration
          in this release; it does not prove that similar uncontrolled copy or historical consumers do not exist.
        </p>

        <dl class="definition-list">
          <div>
            <dt>Declared tokens</dt>
            <dd>{{ usageEvidence.declaredTokenCount }}</dd>
          </div>
          <div>
            <dt>Unused candidates</dt>
            <dd>{{ usageEvidence.unusedCandidateCount }}</dd>
          </div>
          <div>
            <dt>Manifest generated</dt>
            <dd>{{ usageEvidence.generatedAtUtc | date:"medium" }}</dd>
          </div>
        </dl>

        <table *ngIf="terminologyUsageEntries().length > 0">
          <thead>
            <tr>
              <th>Token</th>
              <th>Release status</th>
              <th>Declarations</th>
              <th>Client</th>
              <th>Server</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let entry of terminologyUsageEntries()">
              <td><code>{{ entry.tokenCode }}</code></td>
              <td>{{ entry.declaredUsageCount > 0 ? "Declared use" : "Unused candidate" }}</td>
              <td>{{ entry.declaredUsageCount }}</td>
              <td>{{ entry.clientUsageCount }}</td>
              <td>{{ entry.serverUsageCount }}</td>
            </tr>
          </tbody>
        </table>
      </ng-container>

      <ng-template #usageEvidenceUnavailable>
        <p class="message">
          Deployed usage evidence is temporarily unavailable. Resolution health above remains valid.
        </p>
      </ng-template>
    </section>

    <table *ngIf="terminologyHealthDiagnostics().length > 0">
      <thead>
        <tr>
          <th>Severity</th>
          <th>Token</th>
          <th>Reason</th>
          <th>Related Token</th>
          <th>Dependency Path</th>
          <th>Matches</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let diagnostic of terminologyHealthDiagnostics()">
          <td>{{ diagnostic.severityCode }}</td>
          <td><code>{{ diagnostic.tokenCode }}</code></td>
          <td><code>{{ diagnostic.reasonCode }}</code></td>
          <td><code>{{ diagnostic.relatedTokenCode || "none" }}</code></td>
          <td><code>{{ diagnostic.dependencyPath?.join(" → ") || "none" }}</code></td>
          <td>{{ diagnostic.matchedEntryCount || 0 }}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <div *ngIf="lookupResult() as lookup" class="diagnostic-section" data-testid="lookup-section">
    <header>
      <h2>Lookup Values</h2>
      <span [class]="'badge ' + lookup.state">{{ lookup.state }}</span>
    </header>
    <p *ngIf="lookup.state !== 'resolved'" class="message">{{ formatMessage(lookup) }}</p>
    <table *ngIf="lookupValues().length > 0">
      <thead>
        <tr>
          <th>Code</th>
          <th>Text</th>
          <th>Order</th>
          <th>Enabled</th>
          <th>Applied Scope</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let value of lookupValues()">
          <td>{{ value.valueCode }}</td>
          <td>{{ value.displayText }}</td>
          <td>{{ value.sortOrder }}</td>
          <td>{{ value.isEnabled ? "Yes" : "No" }}</td>
          <td>{{ value.appliedScopeTypeCode || "base" }} {{ value.appliedScopeKey || "" }}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <div *ngIf="fieldMetadataResult() as fieldMetadataResultValue" class="diagnostic-section" data-testid="field-section">
    <header>
      <h2>Field Metadata</h2>
      <span [class]="'badge ' + fieldMetadataResultValue.state">{{ fieldMetadataResultValue.state }}</span>
    </header>
    <p *ngIf="fieldMetadataResultValue.state !== 'resolved'" class="message">{{ formatMessage(fieldMetadataResultValue) }}</p>
    <table *ngIf="fieldMetadata().length > 0">
      <thead>
        <tr>
          <th>Field</th>
          <th>Required</th>
          <th>Visible</th>
          <th>Editable</th>
          <th>Rule</th>
          <th>Hint</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let field of fieldMetadata()">
          <td>{{ field.fieldCode }}</td>
          <td>{{ field.requiredModeCode }}</td>
          <td>{{ field.visibleModeCode }}</td>
          <td>{{ field.editableModeCode }}</td>
          <td>{{ field.ruleCode || "none" }}</td>
          <td>{{ field.clientHintCode || "none" }}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <div *ngIf="issueCatalogResult() as issueCatalog" class="diagnostic-section" data-testid="issue-section">
    <header>
      <h2>Issue Catalog</h2>
      <span [class]="'badge ' + issueCatalog.state">{{ issueCatalog.state }}</span>
    </header>
    <p *ngIf="issueCatalog.state !== 'resolved'" class="message">{{ formatMessage(issueCatalog) }}</p>
    <table *ngIf="issueEntries().length > 0">
      <thead>
        <tr>
          <th>Issue</th>
          <th>Rule</th>
          <th>Severity</th>
          <th>Blocking</th>
          <th>Hint</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let issue of issueEntries()">
          <td>{{ issue.issueCode }}</td>
          <td>{{ issue.ruleCode || "none" }}</td>
          <td>{{ issue.severityCode }}</td>
          <td>{{ issue.isBlocking ? "Yes" : "No" }}</td>
          <td>{{ issue.clientHandlingHintCode || "none" }}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <div *ngIf="ruleBindingsResult() as ruleBindings" class="diagnostic-section" data-testid="rule-section">
    <header>
      <h2>Rule Bindings</h2>
      <span [class]="'badge ' + ruleBindings.state">{{ ruleBindings.state }}</span>
    </header>
    <p *ngIf="ruleBindings.state !== 'resolved'" class="message">{{ formatMessage(ruleBindings) }}</p>
    <table *ngIf="ruleBindingEntries().length > 0">
      <thead>
        <tr>
          <th>Subject</th>
          <th>Rule</th>
          <th>Type</th>
          <th>Enabled</th>
          <th>Expression</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let binding of ruleBindingEntries()">
          <td>{{ binding.subjectCode }}</td>
          <td>{{ binding.ruleCode }}</td>
          <td>{{ binding.bindingTypeCode }}</td>
          <td>{{ binding.isEnabled ? "Yes" : "No" }}</td>
          <td><code>{{ binding.expressionJson || "none" }}</code></td>
        </tr>
      </tbody>
    </table>
  </div>
</section>
